import React, { useEffect, useRef, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

// VoiceMirror — the optician (Build_Game_Intro §4.3).
//
// "You're not picking a voice. You're finding out who they sound like."
//
// Never a list. Sassy tries something, then something else; the player says
// which is closer. Two cards, one line — the artist's OWN line, from the bio
// the ride just wrote — and each answer trims a pool whose size is never
// shown. The escape hatches are real: "keep looking" steps back up the
// narrowing (a client-side state stack), and walking out unchanged is a
// legitimate ending, because a default voice was assigned before the mirror
// ever opened.
//
// The server owns the pool, the axes, the line and the gate (the A/B sits
// behind the salon's paywall; the default does not). This component owns only
// audio playback and the state stack. Clips are fetched with the bearer token
// and played from blobs — an <audio src> can't carry Authorization.

const call = async (path, body) => {
  const res = await fetch(`${API_BASE}/api/voice-audition/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || `audition ${res.status}`); e.code = data.code; throw e; }
  return data;
};

export default function VoiceMirror({ avatarId, onDone, onNote }) {
  const [phase, setPhase] = useState('opening');   // opening | gated | ab | converged | done | error
  const [line, setLine] = useState('');
  const [pair, setPair] = useState([]);
  const [axis, setAxis] = useState(null);
  const [gatePrice, setGatePrice] = useState(null);
  const [winner, setWinner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);    // voiceId currently sounding
  const [note, setNote] = useState('');

  const stack = useRef([]);          // state history — "keep looking" pops
  const stateRef = useRef(null);
  const clipCache = useRef(new Map());   // voiceId → object URL
  const audioRef = useRef(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const d = await call('start', { avatarId });
        if (dead) return;
        if (d.gated) { setGatePrice(d.price); setPhase('gated'); return; }
        if (d.empty) { setNote('The mirror has nothing to show tonight.'); setPhase('error'); return; }
        setLine(d.line || '');
        if (d.converged) { setWinner(d.converged); setPhase('converged'); return; }
        stateRef.current = d.state; stack.current = [];
        setPair(d.pair || []); setAxis(d.axis || null); setPhase('ab');
      } catch (err) {
        if (!dead) { setNote(err.message); setPhase('error'); }
      }
    })();
    return () => {
      dead = true;
      audioRef.current?.pause();
      for (const url of clipCache.current.values()) URL.revokeObjectURL(url);
    };
  }, [avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchClip(voiceId) {
    let url = clipCache.current.get(voiceId);
    if (url) return url;
    const res = await fetch(`${API_BASE}/api/voice-audition/clip?avatarId=${avatarId}&voiceId=${encodeURIComponent(voiceId)}`, {
      headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
    });
    if (!res.ok) throw new Error('no audio');
    url = URL.createObjectURL(await res.blob());
    clipCache.current.set(voiceId, url);
    return url;
  }

  // PREFETCH the pair the moment it exists, and REVEAL ONLY WHEN ARMED
  // (Khari, 2026-08-03: "the mirror should open when its images are ready —
  // Sassy can talk while the load is happening, to hide the load"). The
  // component mounts silent and invisible; the conversation IS the loading
  // screen. Once the first pair's clips are cached the card appears in the
  // transcript with both ▶s live. allSettled, not all: one dead clip should
  // not hide the mirror forever — its play button explains itself on tap.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!pair.length) return;
    let dead = false;
    // FIRST clip ready = show (Khari, 2026-08-03: "I'm not getting samples,
    // just being told to choose" — each take is a fresh TTS render QUEUED
    // behind Sassy's own line synthesis, and waiting for both left the card
    // invisible while the conversation moved on). Take two keeps warming in
    // the background; the auto-play awaits it in turn. A 20s deadline shows
    // the card no matter what — a dead ▶ explains itself, silence doesn't.
    const deadline = setTimeout(() => { if (!dead) setRevealed(true); }, 20000);
    if (pair[0]?.voiceId) {
      fetchClip(pair[0].voiceId).catch(() => {}).finally(() => { if (!dead) setRevealed(true); });
    }
    if (pair[1]?.voiceId) fetchClip(pair[1].voiceId).catch(() => {});
    return () => { dead = true; clearTimeout(deadline); };
  }, [pair]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the takes appear, bring them into view — and tell the SCENE, so
  // Sassy stops improvising about clips she can't see ("they're warming
  // up…") and starts working with what's actually playing.
  const cardRef = useRef(null);
  const notedRef = useRef(false);
  const visible = revealed || ['gated', 'converged', 'done', 'error'].includes(phase);
  useEffect(() => {
    if (!visible) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (!notedRef.current && phase === 'ab') {
      notedRef.current = true;
      onNote?.('(The voice takes just came up and are playing out loud — let them listen, then ask which one is them.)');
    }
  }, [visible, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  async function play(voiceId) {
    try {
      audioRef.current?.pause();
      setPlaying(voiceId);
      const url = await fetchClip(voiceId);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlaying((p) => (p === voiceId ? null : p));
      await a.play();
    } catch {
      setPlaying(null);
      setNote("That one's not warming up — try the other.");
    }
  }

  // Like play(), but resolves when the clip FINISHES — the auto-audition
  // needs "then" semantics to run One, then Two.
  function playThrough(voiceId) {
    return new Promise((resolve) => {
      (async () => {
        try {
          audioRef.current?.pause();
          setPlaying(voiceId);
          const url = await fetchClip(voiceId);
          const a = new Audio(url);
          audioRef.current = a;
          a.onended = () => { setPlaying((p) => (p === voiceId ? null : p)); resolve(); };
          a.onerror = () => { setPlaying(null); resolve(); };
          await a.play();
        } catch { setPlaying(null); resolve(); }
      })();
    });
  }

  // AUTO-AUDITION (Khari, 2026-08-03: "are you expecting me to press the
  // play button? I hope not"): the moment a pair is on screen it PLAYS —
  // take One, a breath, take Two — exactly what a stylist trying voices on
  // you would do. Each new pair auto-plays once; ▶ remains for replays.
  const autoPlayedRef = useRef(null);
  useEffect(() => {
    if (!revealed || phase !== 'ab' || !pair.length) return undefined;
    const sig = pair.map((v) => v?.voiceId || '').join('|');
    if (autoPlayedRef.current === sig) return undefined;
    autoPlayedRef.current = sig;
    let dead = false;
    (async () => {
      const [one, two] = pair;
      if (one?.voiceId && !dead) await playThrough(one.voiceId);
      if (dead) return;
      await new Promise((r) => setTimeout(r, 350));
      if (two?.voiceId && !dead) await playThrough(two.voiceId);
    })();
    return () => { dead = true; };
  }, [revealed, phase, pair]); // eslint-disable-line react-hooks/exhaustive-deps

  async function choose(voiceId) {
    if (busy) return;
    setBusy(true); setNote('');
    audioRef.current?.pause(); setPlaying(null);
    try {
      stack.current.push({ state: stateRef.current, pair, axis });
      const d = await call('choose', { avatarId, state: stateRef.current, pick: voiceId, axisKey: axis?.key || null });
      stateRef.current = d.state;
      if (d.converged) { setWinner(d.converged); setPhase('converged'); }
      else { setPair(d.pair || []); setAxis(d.axis || null); }
    } catch (err) {
      stack.current.pop();
      setNote(err.code === 'GATE' ? 'Settle up with Sassy first.' : err.message);
    } finally {
      setBusy(false);
    }
  }

  function keepLooking() {
    // Step back up the narrowing — undo the last answer, not a wider server
    // query. From the top of the stack it simply replays the first pair.
    const prev = stack.current.pop();
    if (!prev) { setNote('This is as wide as it goes — pick the closer one.'); return; }
    stateRef.current = prev.state;
    setPair(prev.pair); setAxis(prev.axis);
    if (phase === 'converged') { setWinner(null); setPhase('ab'); }
  }

  async function take() {
    if (!winner || busy) return;
    setBusy(true);
    try {
      await call('commit', { avatarId, voiceId: winner.voiceId });
      onNote?.(`(The takes settled it — from now on, this is how they sound.)`);
      setPhase('done');
      setTimeout(() => onDone?.(), 1200);
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  }

  const walkOut = () => { audioRef.current?.pause(); onDone?.(); };

  // Silent until armed — the load hides behind Sassy's talking.
  if (!visible) return null;

  // NOT "the mirror" (Khari, 2026-08-03: "a mirror... for a voice? …the
  // idea of a mirror for voice was incorrect" — the label promised a look
  // and he went hunting for images). A voice is HEARD: takes + waveforms.
  // The mirror word now belongs to the LOOK render alone.
  return (
    <div style={wrap} ref={cardRef}>
      <div style={head}>
        <div style={{ font: '800 13px system-ui', letterSpacing: 0.4 }}>🎙 YOUR VOICE — the takes</div>
        <button style={x} onClick={walkOut} aria-label="Walk away">×</button>
      </div>

      {phase === 'gated' && (
        <div style={{ padding: '6px 2px' }}>
          <div style={{ font: '600 13px system-ui', lineHeight: 1.5 }}>
            The takes are part of the full treatment{gatePrice ? ` — ${gatePrice} credits, one time` : ''}.
            Settle up with Sassy and she'll find who you sound like.
          </div>
          <button style={{ ...ghostBtn, marginTop: 10 }} onClick={walkOut}>Back to the chair</button>
        </div>
      )}

      {phase === 'ab' && (
        <>
          {line && <div style={lineStyle}>“{line}”</div>}
          <div style={{ ...dim, marginBottom: 8 }}>
            {axis ? `Better ${axis.a}, or ${axis.b}?` : 'This one, or that one?'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {pair.map((v, i) => v && (
              <div key={v.voiceId} style={card}>
                <div style={{ font: '700 13px system-ui', marginBottom: 6 }}>{i === 0 ? 'Take one' : 'Take two'}</div>
                <Wave active={playing === v.voiceId} />
                <button style={playBtn} onClick={() => play(v.voiceId)} aria-label="Play again">
                  {playing === v.voiceId ? '▮▮' : '▶'}
                </button>
                <button style={{ ...pickBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => choose(v.voiceId)}>
                  This one
                </button>
              </div>
            ))}
          </div>
          <style>{'@keyframes vmwave { from { transform: scaleY(0.35) } to { transform: scaleY(1.25) } }'}</style>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'center' }}>
            <button style={ghostBtn} onClick={keepLooking}>Keep looking</button>
            <button style={ghostBtn} onClick={walkOut}>I'm good as I am</button>
          </div>
        </>
      )}

      {phase === 'converged' && winner && (
        <div style={{ textAlign: 'center', padding: '6px 0' }}>
          <div style={dim}>She lets the last take ring out.</div>
          <div style={{ font: '800 17px system-ui', margin: '8px 0 2px' }}>This is you.</div>
          {line && <div style={lineStyle}>“{line}”</div>}
          <button style={{ ...playBtn, margin: '6px auto 12px' }} onClick={() => play(winner.voiceId)}>
            {playing === winner.voiceId ? '▮▮' : '▶'}
          </button>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button style={{ ...pickBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={take}>Take it</button>
            <button style={ghostBtn} onClick={keepLooking}>Keep looking</button>
            <button style={ghostBtn} onClick={walkOut}>Walk out as I came</button>
          </div>
        </div>
      )}

      {phase === 'done' && <div style={{ ...dim, textAlign: 'center', padding: '10px 0' }}>Settled.</div>}
      {phase === 'error' && <div style={{ ...dim, color: '#ff8a9c' }}>{note || 'No takes tonight.'}</div>}
      {note && phase !== 'error' && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d', marginTop: 8 }}>{note}</div>}
    </div>
  );
}

// IN-FLOW, not fixed (Khari, 2026-08-03: "those results should show in the
// chat log — no need for a new modal"): the mirror renders inside SceneDock's
// transcript as a special bubble. The old fixed panel also LOST CLICKS — the
// room's Done/exit chrome stacked on top of its buttons at some viewport
// sizes, which read as "the clips did not load."
// A voice, drawn as a voice: a little waveform that breathes while its take
// plays (Khari, 2026-08-03 — "the voice can be images of audio waves and a
// simple play button"). Pure CSS, no assets.
function Wave({ active }) {
  const bars = [5, 11, 7, 14, 9, 16, 8, 13, 6, 12, 9, 15, 7, 10];
  return (
    <div style={{ display: 'flex', gap: 2.5, alignItems: 'center', height: 18, marginBottom: 2 }} aria-hidden="true">
      {bars.map((h, i) => (
        <span key={i} style={{
          width: 2.5, height: h, borderRadius: 2,
          background: 'rgba(240,166,192,0.8)',
          opacity: active ? 1 : 0.45,
          transformOrigin: 'center',
          animation: active ? `vmwave 0.85s ease-in-out ${i * 0.06}s infinite alternate` : 'none',
        }} />
      ))}
    </div>
  );
}

const wrap = {
  alignSelf: 'stretch', width: '100%', boxSizing: 'border-box',
  padding: '12px 14px', margin: '2px 0',
  background: 'rgba(240,166,192,0.07)', border: '1px solid rgba(240,166,192,0.4)',
  borderRadius: 12, color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
};
const head = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 };
const x = { cursor: 'pointer', background: 'none', border: 'none', color: '#ffe9c4', fontSize: 18, lineHeight: 1, padding: 2 };
const dim = { font: '600 12px system-ui', opacity: 0.6 };
const lineStyle = { font: 'italic 600 12.5px system-ui', opacity: 0.85, margin: '4px 0 10px', lineHeight: 1.45 };
const card = {
  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  padding: '12px 8px', borderRadius: 12,
  border: '1px solid rgba(240,166,192,0.35)', background: 'rgba(255,255,255,0.05)',
};
const playBtn = {
  width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(240,166,192,0.6)',
  background: 'rgba(240,166,192,0.15)', color: '#ffe9c4', fontSize: 16, cursor: 'pointer',
  display: 'grid', placeItems: 'center',
};
const pickBtn = {
  padding: '7px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
  font: '700 12px system-ui', background: 'linear-gradient(180deg,#f0a6c0,#ff5e78)', color: '#1a0f00',
};
const ghostBtn = {
  padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
  border: '1px solid rgba(255,233,196,0.3)', background: 'none',
  color: 'rgba(255,233,196,0.8)', font: '700 11.5px system-ui',
};
